import { useState, useRef, useContext, useMemo } from 'react';
import { I18nextContext, useTranslation } from 'gatsby-plugin-react-i18next';
import { I18NextContext as I18NextContextType } from 'gatsby-plugin-react-i18next/dist/types'
import { ArrayParam, useQueryParam } from 'use-query-params';
import { MultiSelect } from 'react-multi-select-component';
import useSearch from '../../utils/hooks/useSearch';

import { PaginationProvider } from '../../utils/ctx/PaginationContext';
import generateWatchProjectImage from '../../utils/generateWatchProjectImage';

import ProductCard, { ProductCardProps } from '../ProductCard';
import { SearchResult } from '../SellingHero/SearchInput';
import Pagination from '../Pagination';
import createSelectModelOptions from '../../utils/createSelectModelOptions';
import { SELLING_PRODUCT_FORM_ROUTE, SELLING_PRODUCT_VALORACION_ROUTE } from '../../utils/routes/const';

const SEARCH_RESULTS_PER_PAGE = 32;

const SearchGrid = ({ models, brands }) => {
  const { t } = useTranslation();
  const [brandsFilters = [], setBrandsFilters] = useQueryParam('brand', ArrayParam);
  const [modelsFilters = [], setModelsFilters] = useQueryParam('model', ArrayParam);
  const [page, setPage] = useState(1);
  const paginationTopScrollRef = useRef(null);

  if (typeof window === 'undefined') {
    return null;
  }

  const selectOptions = createSelectModelOptions(models, brandsFilters);

  const handleBrandsFiltersChange = (e) =>
    brandsFilters.includes(e.target.id) ? setBrandsFilters(brandsFilters.filter(f => f !== e.target.id)) : setBrandsFilters([...brandsFilters, e.target.id]);

  const handleModelsFiltersChange = (selectedModels) =>
    setModelsFilters(selectedModels.map(model => model.value));

  const params = new URLSearchParams(window.location.search);
  const { data, isLoading } = useSearch(params.get('q'), true, { limit: SEARCH_RESULTS_PER_PAGE, page });

  return (
    <section className="py-12 px-4 lg:px-8 mt-12 bg-gray-100">
      <div className="max-w-9xl mx-auto">
        <h1 className='font-cirka text-4xl ml-4 mb-8 '>{t('Search Results for ')}<span className="text-primary">{params.get('q')}</span></h1>
        <details className="flex w-full mb-14 p-5" open ref={paginationTopScrollRef}>
          <summary className="text-xl text-primary">Filtrar relojes</summary>
          <div className="my-4">
            <b>Por marca</b>
            <div className="flex flex-col h-112 overflow-y-hidden flex-wrap">
              {brands.map(brand => (
                <div key={brand.name} className="my-2 flex">
                  <input type="checkbox" id={brand.name} onChange={handleBrandsFiltersChange} />
                  <label htmlFor={brand.name}>{brand.name}</label>
                </div>
              ))}
            </div>
            <b>Por Modelo</b>
            <MultiSelect
              options={selectOptions}
              value={modelsFilters.map(model => ({ label: model, value: model }))}
              onChange={handleModelsFiltersChange}
              labelledBy="Select"
            />
          </div>
        </details>
        <SearchGridResultsMapper query={params.get('q')} data={data} isLoading={isLoading} />
        <div className="mb-20">
          <PaginationProvider currentPage={page} setPage={setPage} scrollRef={paginationTopScrollRef}>
            <Pagination maxPages={data && Math.ceil(data['hydra:totalItems'] / SEARCH_RESULTS_PER_PAGE)} />
          </PaginationProvider>
        </div>
      </div>
    </section>
  )
}

const defaultMapperFunction = (localeContext: I18NextContextType, result: SearchResult) => {
  const numberFormatter = Intl.NumberFormat(localeContext.language, { style: 'currency', currency: 'EUR', currencyDisplay: 'symbol', minimumFractionDigits: 0 });
  const marketValueFrom = numberFormatter.format(result.marketValueFrom || 0);
  const marketValueTo = numberFormatter.format(result.marketValueTo || 0);
  const slug = result.slug.replace(/^\/|\/$/g, '');

  const productCardResults: ProductCardProps = {
    title: `${result.brand} ${result.model} (${result.referenceNumber})`,
    price: `${marketValueFrom} / ${marketValueTo}`,
    image: generateWatchProjectImage(result.imageUrl, result.referenceNumber),
    link: result.valuation ? SELLING_PRODUCT_VALORACION_ROUTE(slug) : SELLING_PRODUCT_FORM_ROUTE(slug),
    linkStyle: 'my-4 lg:mb-12 line-clamp-2',
    imageWrapperClass: 'px-4 pt-4',
    imageWrapperSubClass: 'border border-3 border-gray',
    textWrapperClass: 'px-4 pb-4 !pt-0',
  };

  return productCardResults;
}

interface SearchGridResultsMapperProps {
  query?: string,
  data: Array<any>,
  isLoading: boolean,
  bgColor?: string,
  gridClasses?: string,
  resultMappingFunction?: (localeContext: I18NextContextType, result: SearchResult) => ProductCardProps
};

export const SearchGridResultsMapper = ({
  query = '',
  data,
  isLoading,
  gridClasses = 'grid xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4 p-5',
  resultMappingFunction = defaultMapperFunction }: SearchGridResultsMapperProps) => {

  if (!query) {
    return (<div>No Search results found</div>)
  }

  if (isLoading) {
    return <div className={gridClasses}>
      {
        (new Array(9)).fill((v) => v).map(() => (
          <LoadingSkeletonItem key={Math.random()} />
        ))
      }
    </div>
  }

  if (!data || !data['hydra:member']) {
    return <div className={gridClasses}>
      {
        (new Array(9)).fill((v) => v).map(() => (
          <LoadingSkeletonItem key={Math.random()} />
        ))
      }
    </div>
  }

  if (data && data['hydra:member'] && data['hydra:member'].length === 0) {
    return <div>No Search Results</div>
  }

  const localeContext = useContext(I18nextContext);
  const results = useMemo(() => data['hydra:member'].map(resultMappingFunction.bind(null, localeContext)), [data]);

  return (
    <div className={`${gridClasses}`} data-test="search-grid">
      {results.map((product: ProductCardProps) => (
        <ProductCard {...product} key={`${typeof product.title === 'string' ? product.title : product.link}-${product.price}`} />
      ))}
    </div>
  )
}

const LoadingSkeletonItem = () => (
  <article className="min-w-40 lg:min-w-80 h-112 border border-primary">
    <span className="h-1/2 flex items-center justify-center skeleton-box" />
    <div className="bg-white p-4 flex flex-col h-1/2" style={{ transform: 'translateZ(10px)' }}>
      <span className="!block h-8 w-full skeleton-box" />
      <div className="flex flex-col mt-auto">
        <span className="h-8 skeleton-box" />
        <a href="/" className="no-animation mt-4 flex items-center">
          <span className="h-8 w-8 flex-shrink-0 rounded-full skeleton-box" />
          <span className="ml-4 h-8 skeleton-box w-full" />
        </a>
      </div>
    </div>
  </article>
)

export default SearchGrid;
